import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-cycle
import { RootState } from '../store';
import { adminDomains } from '../../api';
import {v4 as uuidv4} from 'uuid';
import {storageFactory} from '../../helpers/storage';

const local = storageFactory(() => localStorage);

// const backendURL =
//   !process.env.NODE_ENV || process.env.NODE_ENV === 'development' ? 'https://webinar.black-sheep.ru' : '';
const backendURL =
  process.env.NODE_ENV === 'development' ? `https://${adminDomains[0]}` : `https://${window.location.hostname}`;

const initialState: State = {
  email: '',
  userRegistrationFields: [],
  eventKey: '',
  isLoggedIn: false,
  isAdminLoggedIn: false,
  contentPages: {},
  isRegisterInProgress: false,
  isRegistrationRequired: true,
  userId: '',
};

export const userData = createSlice({
  name: 'userData',
  initialState,
  reducers: {
    setUserEmail: (state, action: PayloadAction<any>) => {
      state.email = action.payload;
    },
    setUserRegistrationFields: (state, action: PayloadAction<any>) => {
      state.userRegistrationFields = action.payload;
    },
    setEventKey: (state, action: PayloadAction<string>) => {
      state.eventKey = action.payload;
    },
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setAdminLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isAdminLoggedIn = action.payload;
    },
    setContentPages: (state, action: PayloadAction<object>) => {
      state.contentPages = {
        backendURL,
        ...action.payload,
      };
    },
    setIsRegisterInProgress: (state, action: PayloadAction<any>) => {
      state.isRegisterInProgress = action.payload;
    },
    setIsRegistrationRequired: (state, action: PayloadAction<boolean>) => {
      state.isRegistrationRequired = action.payload;
    },
    setUserId: (state) => {
      if (state.userId.length) {
        return;
      }

      const storageUserID = local.getItem('_bcuser_identify');
      if (storageUserID) {
        state.userId = storageUserID;
      } else {
        const id = uuidv4();
        state.userId = id;
        local.setItem('_bcuser_identify', id);
      }
    }
  },
});

export const {
  setUserEmail,
  setUserRegistrationFields,
  setEventKey,
  setLoggedIn,
  setAdminLoggedIn,
  setContentPages,
  setIsRegisterInProgress,
  setIsRegistrationRequired,
  setUserId,
} = userData.actions;

export const getUserEmail = (state: RootState) => state.userData.email;
export const getUserId = (state: RootState) => state.userData.userId;
export const getUserRegistrationFields = (state: RootState) => state.userData.userRegistrationFields;
export const getEventKey = (state: RootState) => state.userData.eventKey;
export const getIsLoggedIn = (state: RootState) => state.userData.isLoggedIn;
export const getIsAdminLoggedIn = (state: RootState) => state.userData.isAdminLoggedIn;

export const getContentDataHeader = (state: RootState) => {
  const { logo } = state.userData.contentPages;
  return { backendURL, logo };
};

export const getContentDataIntro = (state: RootState) => {
  const { mainImage, decorationImage, eventName, description, timeStart, mainMobileImage, eventName_en, description_en } = state.userData.contentPages;
  return { backendURL, mainImage, decorationImage, eventName, description, timeStart, mainMobileImage, eventName_en, description_en };
};

export const getContentDataAbout = (state: RootState) => {
  const { confImage, confYoutube, count, confInfoAdditional, confInfo, confInfo_en, confInfoAdditional_en } = state.userData.contentPages;
  return { backendURL, confImage, confYoutube, count, confInfoAdditional, confInfo, confInfo_en, confInfoAdditional_en };
};

export const getContentDataProgram = (state: RootState) => {
  const { program, programSubtitle, programSubtitle_en } = state.userData.contentPages;
  return { backendURL, program, programSubtitle, programSubtitle_en };
};

export const getContentDataSpeakers = (state: RootState) => {
  const result = {
    speakerSubtitle: state.userData.contentPages.speakerSubtitle,
    speakerSubtitle_en: state.userData.contentPages.speakerSubtitle_en,
    allowTargetBeforeStart: state.userData.contentPages.moduleQuestion.allowTargetBeforeStart || false,
  };
  return result;
};

export const getContentDataNews = (state: RootState) => {
  const { news } = state.userData.contentPages;
  return { backendURL, news };
};

export const getContentDataPartners = (state: RootState) => {
  const { partners } = state.userData.contentPages;
  return { backendURL, partners };
};

export const getContentDataContacts = (state: RootState) => state.userData.contentPages.contacts;

export const getContentFooter = (state: RootState) => {
  const { logo, footer, footer_en} = state.userData.contentPages;
  return { backendURL, logo, footer, footer_en };
};

export const getContentDataRegistration = (state: RootState) => {
  const { internalImage } = state.userData.contentPages;
  return { backendURL, internalImage };
};

export const getStaticPageDate = (staticPageId?: string) =>
  createSelector(
    (state: RootState) =>
      state.userData.contentPages.news && state.userData.contentPages.program
        ? [...state.userData.contentPages.news, ...state.userData.contentPages.program]
        : false,
    (state) => ({ ...(state && state.find((news) => news.staticPageId === staticPageId)), backendURL })
  );

export const getScheduleStreams = (state: RootState) => state.userData.contentPages.stream;
export const getHeadData = (state: RootState) => state.userData.contentPages.head;
export const getContentDataStream = (state: RootState) => state.userData.contentPages.broadcast;
export const getIsRegisterInProgress = (state: RootState) => state.userData.isRegisterInProgress;
export const getIsRegistrationRequired = (state: RootState) => state.userData.isRegistrationRequired;
export const getEventParticipationType = (state: RootState) => state.userData.contentPages?.participationType ?? '';

// Exhibition navmenu buttons
export const getExhibitionNavmenuButtons = (state: RootState) => {
  const { exhibitionButton1, exhibitionButton2, exhibitionButtonLive } = state.userData.contentPages;
  return { exhibitionButton1, exhibitionButton2, exhibitionButtonLive };
}

export const getNavmenuButtonsVisibility = (state: RootState) => {
  const { exhibitionButton1, exhibitionButton2, exhibitionButtonLive } = state.userData.contentPages;
  const btns = [exhibitionButton1, exhibitionButton2, exhibitionButtonLive];
  const isLoggedIn = state.userData.isLoggedIn;
  return btns.map(btn => {
    if (isLoggedIn && btn?.showAfterAuth) return true;
    if (!isLoggedIn && btn?.showUnauthorized) return true;
    return false;
  })
}

export const getIsNavmenuButtonsVisibile = (state: RootState) => {
  return getNavmenuButtonsVisibility(state).reduce((prevBtnVis, currBtnVis) => prevBtnVis || currBtnVis);
}

export const getExhibitionMainPage = (state: RootState) => {
  const exhibitionMainPage = !!state.userData.contentPages.exhibitionPages && state.userData.contentPages.exhibitionPages.find((page: any) => page.type === 'double.header.list');
  return exhibitionMainPage;
}

export const getDemozoneThemeList = (state: RootState) => {
  const demozoneThemeList = !!state.userData.contentPages.exhibitionPages && state.userData.contentPages.exhibitionPages.filter((page: any) => page.type === 'custom.theme.list');
  return demozoneThemeList;
}

export const getDemozoneCustomeThemes = (state: RootState) => {
  let demozoneCustomeThemes: any = [];
  if (state.userData.contentPages.exhibitionPages) {
    demozoneCustomeThemes = state.userData.contentPages.exhibitionPages.filter((page: any) => page.type === 'custom.theme')
  }
  return demozoneCustomeThemes;
}

export const getSponsorPages = (state: RootState) => {
  let sponsorPages: any = [];
  if (state.userData.contentPages.exhibitionPages) {
    sponsorPages = state.userData.contentPages.exhibitionPages.filter((page: any) => page.type === 'partner')
  }
  return sponsorPages;
}

export const getContentDataTextBlocks = (state: RootState) => {
  const { textBlocks } = state.userData.contentPages;
  return textBlocks as ITextBlocks;
}

export const getPrivateEventState = (state: RootState): boolean => {
  return state.userData.contentPages?.private ?? false;
}

export const getMiniLandingData = (state: RootState) => {
  const {backendURL, minilanding } = state.userData.contentPages;
  return {backendURL, minilanding };
}

const userDataReducer = userData.reducer;
export default userDataReducer;

interface State {
  email: string;
  userRegistrationFields: UserRegistrationField[];
  eventKey: string;
  isLoggedIn: boolean;
  isAdminLoggedIn: boolean;
  contentPages: ContentPages;
  isRegisterInProgress: boolean;
  isRegistrationRequired: boolean;
  userId: string;
}

export interface UserRegistrationField {
  field?: { name?: keyof UserInfo };
  eventId?: number;
}

export interface UserInfo {
  Dolgnost?: string;
  city?: string;
  company?: string;
  email?: string;
  eventKey?: string;
  gender?: string;
  name?: string;
  patronymic?: string;
  phone?: string;
  surname?: string;
}
export interface ExhibitionButton {
  name: string;
  name_en?: string;
  page: string;
  pageType: string;
  showAfterAuth?: boolean;
  showUnauthorized?: boolean;
}
interface ContentPages {
  eventName?: string;
  description?: string;
  mainImage?: string;
  mainMobileImage?: string;
  backendURL?: string;
  logo?: string;
  internalImage?: string;
  decorationImage?: string;
  confYoutube?: string;
  confImage?: string;
  count?: { text: string; value: string; textEn?: string; valueEn?: string; }[];
  confInfo?: string;
  confInfoAdditional?: string;
  programSubtitle?: string;
  program?: Program[];
  speakerSubtitle?: string;
  news?: Program[];
  partners?: { image: string; label: string; title: string; size: 'small' | 'big' }[];
  contacts?: { email: string; phone: string };
  footer?: string;
  stream?: any[];
  head?: object;
  broadcast?: BroadCastType;
  timeStart?: number;
  exhibitionButton1?: ExhibitionButton;
  exhibitionButton2?: ExhibitionButton;
  exhibitionButtonLive?: ExhibitionButton;
  exhibitionPages?: Array<any>;
  eventName_en?: string;
  description_en?: string;
  confInfo_en?: string;
  confInfoAdditional_en?: string;
  moduleQuestion?: any;
  programSubtitle_en?: string;
  textBlocks?: object;
  speakerSubtitle_en?: string;
  footer_en?: string;
  private?: boolean;
  minilanding?: IMiniLanding;
  participationType?: string;
}

export interface IMiniLandingBanners {
  alignment: string | null;
  desktopImage: string | null;
  fillColor?: string | null;
  id: string | null;
  link: string | null;
  mobileImage: string | null;
}

export interface IMiniLanding {
  backgroundDesktopImage: string | null;
  backgroundFillColor: string | null;
  backgroundMobileImage: string | null;
  text: string | null;
  text_en: string | null;
  textColor: string | null;
  textSize: number | null;
  banners: IMiniLandingBanners[];
}

interface Program {
  title: string;
  description: string;
  image: string;
  staticPageId: string;
}

export interface BroadCastType {
  text: string;
  tag1: string;
  tag2: string;
  tag3: string;
  color: string;
  desktopImage: string;
  mobileImage: string;
  text_en?: string;
  tag1_en?: string;
  tag2_en?: string;
  tag3_en?: string;
}

export interface exhibitionMainPageData {
  blocks: Array<object>;
  id: string;
  type: string;
}

export interface ITextBlock {
  content: string;
  content_en: string;
  title: string;
  title_en: string;
}
export interface ITextBlocks {
  block1: ITextBlock;
  block2: ITextBlock;
  block3: ITextBlock;
}
